import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, KeyRound, LogOut, User as UserIcon, CreditCard } from 'lucide-react';
import type { User } from '@/types/user';
import './UserMenu.scss';

interface Props {
  user: User;
  /** Fires when the "Change password" item is activated. Parent opens
   *  the change-password modal; this component just closes the menu. */
  onChangePasswordClick: () => void;
  /** Fires when "Sign out" is activated. Parent opens the confirm dialog
   *  + handles the actual logout + navigation. */
  onSignOutClick: () => void;
}

interface MenuItemContentProps {
  icon: ReactNode;
  label: string;
  description?: string;
}

// Inner layout shared by every menu row regardless of whether the row is
// a <Link> (navigates) or a <button> (callback). Keeps the visual
// contract in one place.
const MenuItemContent = ({ icon, label, description }: MenuItemContentProps) => (
  <>
    <span className="user-menu__item-icon" aria-hidden="true">{icon}</span>
    <span className="user-menu__item-text">
      <span className="user-menu__item-label">{label}</span>
      {description && <span className="user-menu__item-desc">{description}</span>}
    </span>
  </>
);

/**
 * Topbar user-menu dropdown.
 *
 * Trigger: the inline user chip (name + email + chevron). Click opens
 * a small overlay listing account routes + a sign-out at the bottom.
 *
 * Items:
 *   - Profile               → /profile           (navigates)
 *   - Change password       → onChangePasswordClick (opens modal)
 *   - Billing               → /billing           (navigates)
 *   ────────────────────────
 *   - Sign out              → onSignOutClick (parent opens confirm)
 *
 * Production-grade behaviour:
 *   - Click outside closes (mousedown on document so item activation fires first).
 *   - Esc closes + returns focus to the trigger.
 *   - All interactive children are real <a>/<button>, so screen readers
 *     get the right roles for free; only `role="menu"`/`role="menuitem"`
 *     are added so AT recognises the grouping.
 *
 * Scoped to the topbar — if a second consumer ever appears, lift to
 * components/ui/ as a generic <DropdownMenu>. Premature abstraction
 * today.
 */
export const UserMenu = ({ user, onChangePasswordClick, onSignOutClick }: Props) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Close on click-outside. mousedown beats click so a click on a menu
  // item still gets to fire its handler before the menu unmounts.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Esc closes + returns focus to the trigger so keyboard users don't
  // lose their place.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={open ? 'user-menu__trigger user-menu__trigger--open' : 'user-menu__trigger'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="user-menu__trigger-text">
          <span className="user-menu__name">
            {user.first_name} {user.last_name}
          </span>
          <span className="user-menu__email">{user.work_email}</span>
        </span>
        <ChevronDown size={16} aria-hidden="true" className="user-menu__chev" />
      </button>

      {open && (
        <div className="user-menu__panel" role="menu" aria-label="Account menu">
          <header className="user-menu__panel-head" aria-hidden="true">
            <span className="user-menu__name">
              {user.first_name} {user.last_name}
            </span>
            <span className="user-menu__email">{user.work_email}</span>
          </header>

          <div className="user-menu__group">
            <Link
              to="/profile"
              role="menuitem"
              className="user-menu__item"
              onClick={close}
            >
              <MenuItemContent
                icon={<UserIcon size={14} />}
                label="Profile"
                description="Account details + Telegram alerts"
              />
            </Link>

            <button
              type="button"
              role="menuitem"
              className="user-menu__item"
              onClick={() => {
                close();
                onChangePasswordClick();
              }}
            >
              <MenuItemContent
                icon={<KeyRound size={14} />}
                label="Change password"
                description="Update your sign-in password"
              />
            </button>

            <Link
              to="/billing"
              role="menuitem"
              className="user-menu__item"
              onClick={close}
            >
              <MenuItemContent
                icon={<CreditCard size={14} />}
                label="Billing"
                description="Plan, invoices, payment method"
              />
            </Link>
          </div>

          <div className="user-menu__divider" role="separator" />

          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--danger"
            onClick={() => {
              close();
              onSignOutClick();
            }}
          >
            <MenuItemContent
              icon={<LogOut size={14} />}
              label="Sign out"
              description="End this dashboard session"
            />
          </button>
        </div>
      )}
    </div>
  );
};
